import { useRef, useState, useEffect, type FC, Suspense } from "react";
import { Canvas, useFrame, extend } from "@react-three/fiber";
import { useFBO, shaderMaterial } from "@react-three/drei";
import { TransitionShader } from "@tennr/frontend/components/Noise/TransitionShader";
import { Color, ColorManagement, Scene } from "three";
import { usePageScrollProgress } from "@tennr/frontend/hooks/useScrollProgress";

// enable color management
ColorManagement.enabled = true;

// create scenes for transition
const sceneA = new Scene();
const sceneB = new Scene();

// initialize scene backgrounds
sceneA.background = new Color().setRGB(0.69, 0.35, 0.31);
sceneB.background = new Color().setRGB(0.79, 0.71, 0.7);

// create the transition material
const TransitionMaterial = shaderMaterial(
  TransitionShader.uniforms,
  TransitionShader.vertexShader,
  TransitionShader.fragmentShader
);

// extend Three.js with custom material
extend({ TransitionMaterial });

// add TypeScript support for custom material
declare module "react" {
  interface IntrinsicElements {
    transitionMaterial: JSX.IntrinsicElements["mesh"] & {
      mapA?: any;
      mapB?: any;
      iTime?: number;
      progress?: number;
      transitionMode?: number;
      direction?: number[];
      fog?: boolean;
      toneMapped?: boolean;
    };
  }
}

export type ColorStop = { color: string | number[]; progress: number };

interface TransitionRendererProps {
  colorStops: ColorStop[];
  scrollProgress: number;
}

const TransitionRenderer: FC<TransitionRendererProps> = ({ colorStops, scrollProgress }) => {
  const mat = useRef<any>(null);
  const sceneAFBO = useFBO();
  const sceneBFBO = useFBO();
  const [transitionMode, setTransitionMode] = useState(0); // Start with simple blur
  const [direction, setDirection] = useState([1, 1]); // Equal direction to avoid masking

  // track previous progress to determine scroll direction
  const prevProgress = useRef(scrollProgress);
  const hasIncremented = useRef(false);
  const numTransitions = 9; // Number of transition modes
  const directions = [
    [0, 1], // up
    [1, 1], // up-right
    [1, 0], // right
    [1, -1], // down-right
    [0, -1], // down
    [-1, -1], // down-left
    [-1, 0], // left
    [-1, 1], // up-left
  ];
  const numDirections = directions.length;

  useFrame(({ gl, camera, clock }, delta) => {
    if (!mat.current || delta < 0.0) return;

    // get latest scroll progress
    const progress = scrollProgress;

    // find the two stops that bracket the current progress
    let lowerIdx = 0;
    for (let i = 0; i < colorStops.length - 1; i++) {
      if (progress >= colorStops[i].progress && progress <= colorStops[i + 1].progress) {
        lowerIdx = i;
        break;
      }
      if (progress > colorStops[colorStops.length - 1].progress) {
        lowerIdx = colorStops.length - 2;
      }
    }
    const stopA = colorStops[lowerIdx];
    const stopB = colorStops[lowerIdx + 1];
    const segmentProgress = (progress - stopA.progress) / (stopB.progress - stopA.progress);

    // set scene backgrounds every frame based on current stops
    let colorAArray: number[];
    if (typeof stopA.color === "string") {
      // convert hex string to RGB array
      const colorA = new Color(stopA.color);
      colorAArray = [colorA.r, colorA.g, colorA.b];
    } else {
      colorAArray = stopA.color;
    }

    let colorBArray: number[];
    if (typeof stopB.color === "string") {
      // convert hex string to RGB array
      const colorB = new Color(stopB.color);
      colorBArray = [colorB.r, colorB.g, colorB.b];
    } else {
      colorBArray = stopB.color;
    }

    // set scene backgrounds
    sceneA.background = new Color().setRGB(colorAArray[0], colorAArray[1], colorAArray[2]);
    sceneB.background = new Color().setRGB(colorBArray[0], colorBArray[1], colorBArray[2]);

    // render scenes to FBO
    gl.setRenderTarget(sceneAFBO);
    gl.render(sceneA, camera);
    gl.setRenderTarget(sceneBFBO);
    gl.render(sceneB, camera);
    gl.setRenderTarget(null);

    // check if we've hit a stop threshold and should change transition mode
    if (segmentProgress >= 0.99 && !hasIncremented.current) {
      hasIncremented.current = true;
      setTransitionMode((prev) => {
        // Keep at simple blur for now
        return 0;
        
        // Transition cycling logic (commented out for now)
        // let next = (prev + 1) % numTransitions;
        // // Skip problematic transitions: directional (1,3), wipe (4,5)
        // if (next === 1 || next === 3 || next === 4 || next === 5) {
        //   next = next === 1 ? 2 : 6;
        // }
        // return next;
      });

      // determine scroll direction
      const scrollDirection = progress > prevProgress.current ? 1 : -1;
      setDirection((prev) => {
        // Reset to equal direction for masking
        return [1, 1];
        
        // Direction cycling logic (commented out for now)
        // const currentIdx = directions.findIndex((d) => d[0] === prev[0] && d[1] === prev[1]);
        // const nextIdx = (currentIdx + scrollDirection + numDirections) % numDirections;
        // return directions[nextIdx];
      });
    } else if (segmentProgress < 0.9) {
      hasIncremented.current = false;
    }

    // update material uniforms
    mat.current.mapA = sceneAFBO.texture;
    mat.current.mapB = sceneBFBO.texture;
    mat.current.progress = segmentProgress;
    mat.current.iTime = clock.getElapsedTime();
    mat.current.transitionMode = transitionMode;
    mat.current.direction = direction;

    // update previous progress
    prevProgress.current = progress;
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <transitionMaterial ref={mat} fog={false} toneMapped={false} />
    </mesh>
  );
};

interface WebGLBackgroundProps {
  colorStops: ColorStop[];
}

export const WebGLBackground: FC<WebGLBackgroundProps> = ({ colorStops }) => {
  const scrollProgress = usePageScrollProgress();
  const [progress, setProgress] = useState(0);

  // Update progress from motion value
  useEffect(() => {
    const unsubscribe = scrollProgress.on("change", (latest) => {
      setProgress(latest);
    });
    return unsubscribe;
  }, [scrollProgress]);

  return (
    <div className="webgl-background">
      <Suspense fallback={null}>
        <Canvas
          className="webgl-canvas"
          dpr={[1, 2]}
          camera={{ position: [0, 0, 1], fov: 50 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: -1,
          }}
          gl={{ antialias: false }}
        >
          <TransitionRenderer colorStops={colorStops} scrollProgress={progress} />
        </Canvas>
      </Suspense>
    </div>
  );
};
